import React, { useState } from "react";
import { CATEGORY_LABELS, REALM_GROUPS } from "../lib/api";
import { GROUP_COLORS, RealmIcon } from "../lib/realmMeta";
import { ChevronDown } from "lucide-react";

// Accordion realm picker: groups are collapsed by default so you never face all realms at once.
// Groups that already hold a selection open automatically.
export const DomainPicker = ({ value = [], onChange, max = 5 }) => {
  const [open, setOpen] = useState(() => {
    const init = {};
    REALM_GROUPS.forEach((g) => { if (g.domains.some((d) => value.includes(d))) init[g.key] = true; });
    if (Object.keys(init).length === 0 && REALM_GROUPS[0]) init[REALM_GROUPS[0].key] = true;
    return init;
  });
  const toggleGroup = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const toggle = (k) => {
    if (value.includes(k)) onChange(value.filter((x) => x !== k));
    else if (value.length < max) onChange([...value, k]);
  };
  return (
    <div data-testid="domain-picker" className="space-y-2">
      {REALM_GROUPS.map((g) => {
        const gc = GROUP_COLORS[g.key] || "#14A89A";
        const isOpen = !!open[g.key];
        const selCount = g.domains.filter((d) => value.includes(d)).length;
        return (
          <div key={g.key} className="border border-white/10 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => toggleGroup(g.key)}
              data-testid={`domain-group-${g.key}`}
              className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/5 transition-colors"
            >
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: gc }} />
              <span className="font-body text-[11px] font-medium tracking-[0.18em] uppercase" style={{ color: gc }}>{g.label}</span>
              {selCount > 0 && <span className="font-body text-[10px] text-zinc-400">· {selCount} selected</span>}
              <ChevronDown size={15} className={`ml-auto text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <div className="flex flex-wrap gap-2 px-3 pb-3 pt-1">
                {g.domains.map((k) => {
                  const on = value.includes(k);
                  const disabled = !on && value.length >= max;
                  return (
                    <button
                      type="button"
                      key={k}
                      onClick={() => toggle(k)}
                      disabled={disabled}
                      data-testid={`domain-chip-${k}`}
                      style={on ? { borderColor: gc, color: gc, background: `${gc}1A` } : {}}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-body text-xs border transition-colors ${
                        on ? "" : disabled ? "border-white/10 text-zinc-700 cursor-not-allowed" : "border-white/15 text-zinc-400 hover:text-white"
                      }`}
                    >
                      <RealmIcon realm={k} size={13} className="shrink-0" style={on ? { color: gc } : { color: gc, opacity: 0.7 }} />
                      {CATEGORY_LABELS[k]}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      <p className="font-body text-[11px] pt-1" data-testid="domain-picker-hint">
        {value.length === 0
          ? <span className="text-[#14A89A]">Pick at least one realm to continue · the first is your primary realm</span>
          : <span className="text-zinc-500">{value.length}/{max} selected · the first you pick is your primary realm</span>}
      </p>
    </div>
  );
};
